import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { onSuccessInvalidate } from '@/hooks/invalidation'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import type { QueryDef } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'
import { api } from '@/services/api'
import { getNotifications, getUnreadCount } from '@/services/api/notifications'
import type { NotificationsResponse } from '@/types'

function getPollingInterval(): number | false {
  if (typeof window === 'undefined' || !('Notification' in window)) return 86400000

  if (Notification.permission === 'granted') {
    // Push notifications handle updates; no polling needed
    return false
  }

  // Not yet asked or denied; poll once daily
  return 86400000
}

const notificationsQueries = {
  list: {
    queryKey: (limit: number) => ['notifications', limit] as const,
    queryFn: (limit: number) => getNotifications(limit),
    staleTime: 1000 * 60 * 2, // 2 minutes
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getNotifications>>, [number]>,
  unreadCount: {
    queryKey: () => ['notifications', 'unreadCount'] as const,
    queryFn: getUnreadCount,
    auth: 'required',
    refetchInterval: getPollingInterval,
  } satisfies QueryDef<Awaited<ReturnType<typeof getUnreadCount>>>,
}

export function useNotifications(limit = 50, options?: { enabled?: boolean }) {
  return useDefinedQuery(notificationsQueries.list, limit, { enabled: options?.enabled ?? true })
}

export function useUnreadCount() {
  const queryClient = useQueryClient()

  // Listen for push notification messages from the service worker
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
      }
    }
    navigator.serviceWorker?.addEventListener('message', handler)
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handler)
    }
  }, [queryClient])

  return useDefinedQuery(notificationsQueries.unreadCount)
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => api.markNotificationRead(id, idempotencyKey),
    onMutate: async ({ id }: Vars) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] })

      const previousQueries = queryClient.getQueriesData<NotificationsResponse>({ queryKey: ['notifications'] })

      queryClient.setQueriesData<NotificationsResponse>({ queryKey: ['notifications'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          notifications: old.notifications.map((n) =>
            n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n,
          ),
          unreadCount: Math.max(0, old.unreadCount - 1),
        }
      })

      queryClient.setQueryData<{ unreadCount: number }>(notificationsQueries.unreadCount.queryKey(), (old) =>
        old ? { unreadCount: Math.max(0, old.unreadCount - 1) } : old,
      )

      return { previousQueries }
    },
    onError: (_err, _vars, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
  return withIdempotency(mutation)
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  type Vars = { idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ idempotencyKey }: Vars = {}) => api.markAllNotificationsRead(idempotencyKey),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] })

      const previousQueries = queryClient.getQueriesData<NotificationsResponse>({ queryKey: ['notifications'] })

      const now = new Date().toISOString()
      queryClient.setQueriesData<NotificationsResponse>({ queryKey: ['notifications'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          notifications: old.notifications.map((n) => (n.read ? n : { ...n, read: true, readAt: now })),
          unreadCount: 0,
        }
      })

      queryClient.setQueryData<{ unreadCount: number }>(notificationsQueries.unreadCount.queryKey(), { unreadCount: 0 })

      return { previousQueries }
    },
    onError: (_err, _vars, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
  return withIdempotency(mutation, (vars?: Vars) => vars ?? {})
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => api.deleteNotification(id, idempotencyKey),
    ...onSuccessInvalidate(queryClient, 'notifications'),
  })
  return withIdempotency(mutation)
}
